<template>
  <div class="favorites-panel">
    <SidepanelHeader icon="heart" :count="favoriteDocumentsCount" title-key="common.saved" />
    <div class="delete-container" v-if="favoriteDocumentsCount > 0">
      <ButtonDeleteSidepanelItems
        :count="favoriteDocumentsCount"
        :isLoading="isDataLoading"
        @on-click="openConfirmPopup"
      />
    </div>
    <NoDataComponent
      v-if="favoriteDocumentsCount === 0"
      title-key="favorites.noSavedDocuments"
      description-key="favorites.savedDocumentsAppearHere"
    />
    <div class="documents-list" v-if="!isDataLoading">
      <FavoriteDocumentCard
        v-for="(reference, index) in favoriteDocuments"
        :key="reference.externalId || reference"
        :reference="reference"
        :index="index"
        @moveDocument="moveDocument"
      />
    </div>
    <CardsListSkeletonLoader v-else />
  </div>
  <WarningPopup
    :popup-opened="confirmPopupOpened"
    title-key="favorites.deleteSavedDocuments"
    :warning-message="warningMessage"
    confirm-text-key="modals.confirmDeleteText"
    @update:open="confirmPopupOpened = false"
    @execute:action="deleteAll"
  />
</template>

<script>
import { computed, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n/dist/vue-i18n';

import { COLLECTIONS } from '~/constants/collections';

import useCollections from '~/composables/useCollections';
import FavoriteDocumentCard from '~/components/conversation/favoritesPanel/FavoriteDocumentCard';
import CardsListSkeletonLoader from '~/components/conversation/sidepanelGeneralComponents/CardsListSkeletonLoader';
import WarningPopup from '~/components/common/WarningPopup';
import ButtonDeleteSidepanelItems from '~/components/conversation/sidepanelGeneralComponents/ButtonDeleteSidepanelItems.vue';
import SidepanelHeader from '~/components/conversation/sidepanelGeneralComponents/SidepanelHeader.vue';
import NoDataComponent from '~/components/conversation/sidepanelGeneralComponents/NoDataComponent.vue';

export default {
  components: {
    NoDataComponent,
    SidepanelHeader,
    WarningPopup,
    CardsListSkeletonLoader,
    FavoriteDocumentCard,
    ButtonDeleteSidepanelItems,
  },

  setup() {
    const { t } = useI18n();
    const {
      favoriteDocuments,
      favoriteDocumentsCount,
      reorderFavorites,
      deleteAllDocuments,
      setAdditionalDocumentsData,
    } = useCollections();

    const confirmPopupOpened = ref(false);
    const isDataLoading = ref(false);

    const warningMessage = computed(() => t('favorites.warning', { count: favoriteDocumentsCount.value }));

    const openConfirmPopup = () => {
      confirmPopupOpened.value = true;
    };

    const moveDocument = (fromIndex, toIndex) => {
      if (toIndex !== fromIndex) {
        const documents = favoriteDocuments.value;
        const movingDocument = documents.splice(fromIndex, 1)[0];
        documents.splice(toIndex, 0, movingDocument);
        reorderFavorites(documents);
      }
    };

    const deleteAll = () => {
      confirmPopupOpened.value = false;
      deleteAllDocuments();
    };

    onMounted(async () => {
      isDataLoading.value = true;
      await setAdditionalDocumentsData(COLLECTIONS.favorites);
      isDataLoading.value = false;
    });

    return {
      deleteAll,
      favoriteDocumentsCount,
      favoriteDocuments,
      confirmPopupOpened,
      isDataLoading,
      openConfirmPopup,
      moveDocument,
      warningMessage,
    };
  },
};
</script>

<style scoped lang="scss">
* {
  box-sizing: border-box;
}

.favorites-panel {
  @include media-breakpoint-up('sm') {
    display: flex;
  }

  max-height: calc(100vh - 80px);

  // take into consideration the margins
  height: 100%;
  display: none;
  flex-direction: column;
  background-color: $bb-neutral-6;
  border-radius: $bb-spacing-8;
  border: 1px solid #cecdcb;
  box-shadow: 0 0 $bb-spacing-2 0 #0000001a;
  align-items: center;
  min-width: 476px;
  max-width: 476px;
  overflow: hidden;
  margin: auto;

  .delete-container {
    padding: $bb-spacing-2 $bb-spacing-4;
    background-color: $bb-neutral-0;
    width: 100%;
  }

  .documents-list {
    padding: $bb-spacing-2 $bb-spacing-4;
    display: flex;
    flex-direction: column;
    gap: $bb-spacing-2;
    width: 100%;
    overflow: auto;

    & > div {
      border-radius: 8px;
    }
  }
}
</style>
